import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ReCaptcha, { ReCaptchaStyles } from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { RefObject } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const hasSessionStorage = () => typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

// Session storage keys
const RECAPTCHA_VERIFIED_KEY = "g-recaptcha-verified";
const RECAPTCHA_TIMESTAMP_KEY = "g-recaptcha-verified-timestamp";

export const getRecaptchaTimeoutMs = (timeoutValue?: string): number => {
    if (!timeoutValue) {
        return 0;
    }
    const minutes = parseFloat(timeoutValue);
    if (Number.isNaN(minutes) || minutes <= 0) {
        return 0;
    }
    return minutes * 60 * 1000; // Convert minutes to milliseconds
};

export const isRecaptchaVerificationExpired = (): boolean => {
    if (!hasSessionStorage()) {
        return true;
    }

    const timestamp = sessionStorage.getItem(RECAPTCHA_TIMESTAMP_KEY);
    if (!timestamp) {
        return true;
    }

    let timeoutMs = 60000;
    const storedTimeout = sessionStorage.getItem("g-recaptcha-timeout-minutes");
    if (storedTimeout) {
        const minutes = parseFloat(storedTimeout);
        if (!Number.isNaN(minutes) && minutes > 0) {
            timeoutMs = minutes * 60 * 1000;
        }
    }

    const elapsed = Date.now() - parseInt(timestamp, 10);
    return elapsed > timeoutMs;
};

export const clearRecaptchaVerification = (): void => {
    if (!hasSessionStorage()) {
        return;
    }

    sessionStorage.removeItem(RECAPTCHA_VERIFIED_KEY);
    sessionStorage.removeItem(RECAPTCHA_TIMESTAMP_KEY);
    sessionStorage.removeItem("g-recaptcha-timeout-minutes");
};

export const isRecaptchaVerified = (): boolean => {
    if (!hasSessionStorage()) {
        return false;
    }

    const isVerified = !!sessionStorage.getItem(RECAPTCHA_VERIFIED_KEY);
    return isVerified && !isRecaptchaVerificationExpired();
};

interface OwnProps {
    isOpen: boolean;
    onClose: () => void;
    onVerify: () => void;
    extendedStyles?: ReCaptchaModalStyles;
    orderStatusLocation?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    return {
        isReCaptchaEnabled: getSettingsCollection(state).websiteSettings.reCaptchaEnabledForOrderStatus,
        isAuthenticated: session.isGuest,
        captchaTimeOutValue: state.context.settings.properties?.recaptchaTimeout,
    };
};

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

export interface ReCaptchaModalStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    description?: TypographyPresentationProps;
    locationGridItem?: GridItemProps;
    locationText?: TypographyPresentationProps;
    reCaptchaGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    verifyButton?: ButtonPresentationProps;
    reCaptcha?: ReCaptchaStyles;
}

export const reCaptchaModalStyles: ReCaptchaModalStyles = {
    headingGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    modal: {
        size: 500,
        cssOverrides: {
            modalTitle: css`
                padding: 20px 30px;
            `,
            modalContent: css`
                padding: 20px 30px !important;
            `,
        },
    },
    container: {
        gap: 10,
    },
    heading: {
        variant: "h5",
        css: css`
            margin-bottom: 0px !important;
        `,
    },
    description: {
        css: css`
            color: #666;
        `,
    },
    locationText: {
        css: css`
            margin-bottom: 20px;
            font-weight: 500;
        `,
    },
    reCaptchaGridItem: {
        width: 12,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    verifyButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    reCaptcha: {
        wrapper: {
            css: css`
                margin-top: -3px;
            `,
        },
    },
};

const ReCaptchaModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onVerify,
    extendedStyles,
    orderStatusLocation,
    validateReCaptcha,
    captchaTimeOutValue,
}) => {
    const [styles] = React.useState(() => mergeToNew(reCaptchaModalStyles, extendedStyles));
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    const handleVerify = async () => {
        const isReCaptchaValid = await validateReCaptcha({
            location: "OrderStatus",
            recaptchaRef,
        });

        if (isReCaptchaValid) {
            if (hasSessionStorage()) {
                sessionStorage.setItem("g-recaptcha-verified", "true");
                sessionStorage.setItem("g-recaptcha-verified-timestamp", Date.now().toString());
                if (captchaTimeOutValue) {
                    sessionStorage.setItem("g-recaptcha-timeout-minutes", captchaTimeOutValue);
                }
            }
            onVerify();
        }
    };

    const handleClose = () => {
        // Reset recaptcha on close
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
        onClose();
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Verification Required")}
            isOpen={isOpen}
            handleClose={handleClose}
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.headingGridItem}>
                    <Typography {...styles.heading}>{translate("Add to Cart Verification")}</Typography>
                </GridItem>
                <GridItem {...styles.descriptionGridItem}>
                    <Typography {...styles.description}>
                        {translate("Please complete the verification below to add items to your cart.")}
                    </Typography>
                </GridItem>

                <GridItem {...styles.reCaptchaGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="OrderStatus" extendedStyles={styles.reCaptcha} />
                </GridItem>
            </GridContainer>
            <GridContainer>
                <GridItem width={12}>
                    <div {...styles.buttonsWrapper}>
                        <Button {...styles.cancelButton} onClick={handleClose}>
                            {translate("Cancel")}
                        </Button>
                        <Button {...styles.verifyButton} onClick={handleVerify}>
                            {translate("Verify")}
                        </Button>
                    </div>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReCaptchaModal);
